import { format, addDays } from "date-fns";
import type { ShiftRow, CreateShiftForm, RepeatMode } from "./types";

/**
 * Normalise an HHMM or HH:MM string to "HH:MM" format.
 * Handles: "0900" → "09:00", "09:00" → "09:00", "900" → "09:00", undefined/null → "00:00"
 */
export const normalizeHHMM = (raw?: string | null): string => {
  if (!raw) return "00:00";
  const cleaned = raw.replace(/[^0-9]/g, "");
  if (cleaned.length === 0) return "00:00";
  if (cleaned.length <= 2) return cleaned.padStart(2, "0") + ":00";
  if (cleaned.length === 3) return "0" + cleaned[0] + ":" + cleaned.slice(1);
  return cleaned.slice(0, 2) + ":" + cleaned.slice(2, 4);
};

export const formatTime12 = (hhmm?: string | null): string => {
  const normalised = normalizeHHMM(hhmm);
  const [h, m] = normalised.split(":").map(Number);
  const ampm = h >= 12 ? "PM" : "AM";
  const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return m === 0 ? `${h12}:00 ${ampm}` : `${h12}:${String(m).padStart(2, "0")} ${ampm}`;
};

export const formatDuration = (decimalHours: number): string => {
  const totalMinutes = Math.round(decimalHours * 60);
  const h = Math.floor(totalMinutes / 60);
  const m = totalMinutes % 60;
  if (m === 0) return `${h}h`;
  return `${h}h ${m}m`;
};

export const NZ_WEEK_STARTS_ON = 1; // Monday

export const BREAK_MINUTE_OPTIONS = [0, 15, 30, 45, 60, 75, 90, 105, 120];

export const defaultCreateShiftForm = (date: Date): CreateShiftForm => ({
  driver_name: "",
  vehicle_number: "__NONE__",
  shift_type_code: "",
  status: "draft",
  date: format(date, "yyyy-MM-dd"),
  start_time: "08:00",
  end_time: "17:00",
  break_minutes: 30,
  driver_visible_notes: "",
  admin_notes: "",
  repeat_mode: "none",
  repeat_end_date: format(addDays(date, 7), "yyyy-MM-dd"),
  repeat_custom_days: [],
  repeat_cycle_days_on: 2,
  repeat_cycle_days_off: 2,
});

export const combineDateAndTime = (dateString: string, timeString: string): string => {
  return `${dateString}T${timeString}:00`;
};

export const calcWorkedHours = (
  startTime: string,
  endTime: string,
  breakMinutes: number
): number => {
  const [sh, sm] = startTime.split(":").map(Number);
  const [eh, em] = endTime.split(":").map(Number);
  let totalMinutes = eh * 60 + em - (sh * 60 + sm);
  if (totalMinutes < 0) totalMinutes += 24 * 60;
  totalMinutes -= breakMinutes;
  return Math.max(0, Math.round((totalMinutes / 60) * 100) / 100);
};

export const getStatusBadgeVariant = (status: ShiftRow["status"]) => {
  switch (status) {
    case "published":
      return "default" as const;
    case "draft":
      return "secondary" as const;
    case "available":
      return "outline" as const;
    case "claimed":
      return "secondary" as const;
    case "assigned":
      return "default" as const;
    case "completed":
      return "default" as const;
    case "cancelled":
      return "destructive" as const;
    default:
      return "secondary" as const;
  }
};

export const getShiftColour = (shift: ShiftRow): string => {
  return shift.colour_hex || shift.shift_type_default_colour_hex || "#3B82F6";
};

export const driverDisplayName = (shift: ShiftRow) => {
  if (shift.driver_firstname && shift.driver_lastname) {
    return `${shift.driver_firstname} ${shift.driver_lastname.charAt(0)}.`;
  }
  return shift.driver_name;
};

export const generateRepeatDates = (
  repeatMode: RepeatMode,
  startDate: string,
  endDate: string,
  customDays: number[],
  cycleDaysOn = 2,
  cycleDaysOff = 2
): string[] => {
  if (repeatMode === "none") return [startDate];

  const dates: string[] = [];
  const start = new Date(startDate + "T00:00:00");
  const end = new Date(endDate + "T00:00:00");

  if (start > end) return [startDate];

  let current = new Date(start);

  // "Cycle" is a rolling N-days-on / M-days-off rotation (e.g. 2 on, 2 off).
  // This is a fixed-length cycle counted from the start date, not tied to
  // calendar weeks, so it doesn't drift the way "every N weeks" does when
  // the cycle length isn't a multiple of 7.
  if (repeatMode === "cycle") {
    const cycleLength = Math.max(1, cycleDaysOn) + Math.max(0, cycleDaysOff);
    let dayOffset = 0;
    while (current <= end) {
      if (dayOffset % cycleLength < cycleDaysOn) {
        dates.push(format(current, "yyyy-MM-dd"));
      }
      current = addDays(current, 1);
      dayOffset += 1;
    }
    return dates;
  }

  while (current <= end) {
    const dateStr = format(current, "yyyy-MM-dd");

    if (repeatMode === "daily") {
      dates.push(dateStr);
      current = addDays(current, 1);
    } else if (repeatMode === "weekly") {
      dates.push(dateStr);
      current = addDays(current, 7);
    } else if (repeatMode === "fortnightly") {
      dates.push(dateStr);
      current = addDays(current, 14);
    } else if (repeatMode === "custom") {
      const jsDay = current.getDay();
      const ourDay = jsDay === 0 ? 6 : jsDay - 1;
      if (customDays.includes(ourDay)) {
        dates.push(dateStr);
      }
      current = addDays(current, 1);
    }
  }

  return dates;
};

export const DAY_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];